import gsap from 'gsap'

/**
 * NavTeleport Module
 *
 * Handles teleporting the triangle nav between container slots
 * based on viewport size and context (hamburger menu, inline, page toggle).
 *
 * This module moves a single nav instance between different DOM locations
 * to avoid ID conflicts and maintain event listener bindings.
 */

export default class NavTeleport {
  constructor(app, opts = {}) {
    this.app = app
    this.opts = {
      // Breakpoint below which mobile behavior kicks in (matches ipad_landscape)
      mobileBreakpoint: 1024,
      ...opts,
    }

    // Track current location
    this.currentSlot = 'aside'

    // References to DOM elements
    this.navContainer = null
    this.slots = {}

    // Track hamburger menu state
    this.hamburgerOpen = false

    // Track page toggle state
    this.pageToggleOpen = false

    // Track drawer state
    this.drawerOpen = false
    this.previousSlotBeforeDrawer = null

    this.init()
  }

  init() {
    // Find the canonical nav container (the triangle-nav-container element)
    this.navContainer = document.querySelector('.triangle-nav-container')

    if (!this.navContainer) {
      console.log('[NavTeleport] No nav container found, skipping initialization')
      return
    }

    // Find all teleport slots
    this.collectSlots()

    // Setup event listeners
    this.setupEventListeners()

    // Initial position based on viewport
    this.updatePosition()

    console.log('[NavTeleport] Initialized with slots:', Object.keys(this.slots))
  }

  collectSlots() {
    const slotElements = document.querySelectorAll('[data-nav-teleport-slot]')

    slotElements.forEach(el => {
      const slotName = el.dataset.navTeleportSlot
      this.slots[slotName] = {
        element: el,
        active: el.dataset.navTeleportActive === 'true',
      }
    })
  }

  setupEventListeners() {
    // Listen for mobile menu open/close via custom events
    window.addEventListener('mobilemenu:open', () => {
      this.hamburgerOpen = true
      this.updatePosition()
    })

    window.addEventListener('mobilemenu:closed', () => {
      this.hamburgerOpen = false
      this.updatePosition(true) // animate when returning from hamburger
    })

    // Listen for page toggle clicks via event delegation
    document.addEventListener('click', e => {
      const trigger = e.target.closest('[data-mobile-nav-trigger]')
      if (trigger) {
        this.handlePageToggleClick(trigger)
      }
    })

    // Resize handler with debounce
    let resizeTimeout
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        this.handleResize()
      }, 100)
    })

    // Floating button click to open drawer
    document.addEventListener('click', e => {
      if (e.target.closest('[data-nav-fab]')) {
        this.openDrawer()
      }
    })

    // Drawer backdrop click to close
    document.addEventListener('click', e => {
      if (e.target.closest('[data-drawer-backdrop]')) {
        this.closeDrawer()
      }
    })

    // Escape key closes drawer
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.drawerOpen) {
        this.closeDrawer()
      }
    })
  }

  handleResize() {
    const isMobile = window.innerWidth < this.opts.mobileBreakpoint

    // If switching from mobile to desktop, reset states
    if (!isMobile) {
      // Close drawer if open
      if (this.drawerOpen) {
        this.closeDrawer()
      }

      this.pageToggleOpen = false

      // Reset page toggle UI if it exists
      const toggleContainer = document.querySelector('[data-mobile-nav-toggle]')
      if (toggleContainer) {
        const trigger = toggleContainer.querySelector('[data-mobile-nav-trigger]')
        const content = toggleContainer.querySelector('[data-mobile-nav-content]')
        const toggleText = trigger?.querySelector('[data-toggle-text]')

        if (content) content.hidden = true
        if (trigger) trigger.setAttribute('aria-expanded', 'false')
        if (toggleText) toggleText.textContent = 'Åpne modellen'
      }
    }

    this.updatePosition()
  }

  handlePageToggleClick(trigger) {
    const toggleContainer = trigger.closest('[data-mobile-nav-toggle]')
    const content = toggleContainer?.querySelector('[data-mobile-nav-content]')
    const toggleText = trigger.querySelector('[data-toggle-text]')

    if (!content) return

    this.pageToggleOpen = !this.pageToggleOpen

    if (this.pageToggleOpen) {
      // Teleport nav into the toggle content area
      this.teleportTo('page-toggle')
      content.hidden = false
      trigger.setAttribute('aria-expanded', 'true')
      if (toggleText) toggleText.textContent = 'Lukk modellen'
    } else {
      content.hidden = true
      trigger.setAttribute('aria-expanded', 'false')
      if (toggleText) toggleText.textContent = 'Åpne modellen'
      // Return nav to aside (it's hidden on mobile anyway)
      this.teleportTo('aside')
    }
  }

  updatePosition(animate = false) {
    const isMobile = window.innerWidth < this.opts.mobileBreakpoint

    if (!isMobile) {
      // Desktop: always in aside
      this.teleportTo('aside')
      return
    }

    // Mobile: respect drawer state first
    if (this.drawerOpen) {
      // Keep in drawer, don't change
      return
    }

    // Mobile logic - determine which slot should have the nav
    if (this.hamburgerOpen && this.slots['hamburger']) {
      this.teleportTo('hamburger')
    } else if (this.pageToggleOpen && this.slots['page-toggle']) {
      this.teleportTo('page-toggle')
    } else if (this.slots['homepage-inline']) {
      // Homepage inline slot - show nav directly on page
      this.teleportTo('homepage-inline', animate)
    } else {
      // Default: stay in aside (hidden on mobile via CSS)
      this.teleportTo('aside', animate)
    }
  }

  teleportTo(slotName, animate = false) {
    if (this.currentSlot === slotName) return
    if (!this.slots[slotName]) {
      console.warn(`[NavTeleport] Slot "${slotName}" not found`)
      return
    }

    const targetSlot = this.slots[slotName]
    const previousSlot = this.currentSlot
    const shouldAnimate = animate && (previousSlot === 'hamburger' || previousSlot === 'drawer')

    // Mark previous slot as inactive
    if (this.slots[this.currentSlot]) {
      this.slots[this.currentSlot].element.dataset.navTeleportActive = 'false'
    }

    // Move the nav container to the new slot
    targetSlot.element.appendChild(this.navContainer)
    targetSlot.element.dataset.navTeleportActive = 'true'

    this.currentSlot = slotName

    // Animate after moving
    if (shouldAnimate) {
      this.animateSlotReveal()
    }

    console.log(`[NavTeleport] Teleported to "${slotName}"`)

    // Dispatch event for other modules to react
    window.dispatchEvent(
      new CustomEvent('navteleport:moved', {
        detail: { slot: slotName },
      })
    )
  }

  /**
   * Animate the nav reveal using GSAP
   * 1. Height animates from 0 (background visible)
   * 2. Content fades in after
   */
  animateSlotReveal() {
    const widgetWrapper = this.navContainer.querySelector('.triangle-nav-widget-wrapper')
    const listNav = this.navContainer.querySelector('.list-nav')

    if (!widgetWrapper) return

    const tl = gsap.timeline()

    // Get all direct children for content fade
    const widgetContent = widgetWrapper.querySelectorAll(':scope > *')
    const listNavContent = listNav ? listNav.querySelectorAll(':scope > *') : []

    // Set initial state - disable CSS transitions and hide immediately
    gsap.set(widgetWrapper, { height: 0, overflow: 'hidden' })
    gsap.set(widgetContent, { opacity: 0, transition: 'none' })
    if (listNav) {
      gsap.set(listNav, { height: 0, overflow: 'hidden' })
      gsap.set(listNavContent, { opacity: 0, transition: 'none' })
    }

    // Phase 1: Animate height (background visible)
    tl.to(widgetWrapper, {
      height: 'auto',
      duration: 0.3,
      ease: 'power2.out',
    })

    if (listNav) {
      tl.to(listNav, {
        height: 'auto',
        duration: 0.3,
        ease: 'power2.out',
      }, '<') // Same time as widget
    }

    // Phase 2: Fade in content
    tl.to(widgetContent, {
      opacity: 1,
      duration: 0.2,
      ease: 'power2.out',
    })

    if (listNav) {
      tl.to(listNavContent, {
        opacity: 1,
        duration: 0.2,
        ease: 'power2.out',
      }, '<') // Same time
    }

    // Clean up inline styles after animation
    tl.call(() => {
      gsap.set(widgetWrapper, { clearProps: 'height,overflow' })
      gsap.set(widgetContent, { clearProps: 'opacity,transition' })
      if (listNav) {
        gsap.set(listNav, { clearProps: 'height,overflow' })
        gsap.set(listNavContent, { clearProps: 'opacity,transition' })
      }
    })
  }

  /**
   * Open the bottom drawer and teleport nav into it
   */
  openDrawer() {
    const drawer = document.querySelector('[data-nav-drawer]')
    if (!drawer) return

    // 1. Close hamburger if open
    if (this.hamburgerOpen) {
      window.dispatchEvent(new CustomEvent('mobilemenu:close'))
      this.hamburgerOpen = false
    }

    // 2. Collapse page toggle if open
    if (this.pageToggleOpen) {
      this.collapsePageToggle()
    }

    // 3. Store current slot for return
    this.previousSlotBeforeDrawer = this.currentSlot

    // 4. Teleport to drawer
    this.teleportTo('drawer')

    // 5. Show drawer with animation
    drawer.hidden = false
    document.body.classList.add('drawer-open')

    // Force reflow, then animate
    drawer.offsetHeight
    drawer.classList.add('is-open')

    this.drawerOpen = true

    console.log('[NavTeleport] Drawer opened')
  }

  /**
   * Close the drawer and return nav to previous slot
   */
  closeDrawer() {
    const drawer = document.querySelector('[data-nav-drawer]')
    const fab = document.querySelector('.mobile-nav-fab')
    if (!drawer || !this.drawerOpen) return

    // 1. Start close animation
    drawer.classList.remove('is-open')

    // 2. After animation, hide and return nav
    setTimeout(() => {
      drawer.hidden = true

      // Prepare FAB for fade-in: add is-hidden before removing drawer-open
      if (fab) {
        fab.classList.add('is-hidden')
      }

      document.body.classList.remove('drawer-open')

      // Return nav to previous slot (usually 'aside' since page-toggle was collapsed)
      this.teleportTo(this.previousSlotBeforeDrawer || 'aside', true)

      this.drawerOpen = false
      this.previousSlotBeforeDrawer = null

      // Fade in FAB after a brief moment
      if (fab) {
        requestAnimationFrame(() => {
          fab.classList.remove('is-hidden')
        })
      }

      console.log('[NavTeleport] Drawer closed')
    }, 300) // Match CSS transition duration
  }

  /**
   * Collapse the page toggle without triggering full toggle logic
   */
  collapsePageToggle() {
    const toggleContainer = document.querySelector('[data-mobile-nav-toggle]')
    if (!toggleContainer) return

    const trigger = toggleContainer.querySelector('[data-mobile-nav-trigger]')
    const content = toggleContainer.querySelector('[data-mobile-nav-content]')
    const toggleText = trigger?.querySelector('[data-toggle-text]')

    if (content) content.hidden = true
    if (trigger) trigger.setAttribute('aria-expanded', 'false')
    if (toggleText) toggleText.textContent = 'Åpne modellen'

    this.pageToggleOpen = false
  }

  /**
   * Called after view transitions to re-collect slots
   * since the DOM may have been replaced
   */
  refresh() {
    // Re-find the nav container
    this.navContainer = document.querySelector('.triangle-nav-container')

    if (!this.navContainer) {
      console.log('[NavTeleport] No nav container found after refresh')
      return
    }

    // Reset state
    this.currentSlot = 'aside'
    this.slots = {}
    this.pageToggleOpen = false
    this.drawerOpen = false
    this.previousSlotBeforeDrawer = null

    // Hide drawer if it exists (in case of page navigation while open)
    const drawer = document.querySelector('[data-nav-drawer]')
    if (drawer) {
      drawer.hidden = true
      drawer.classList.remove('is-open')
    }
    document.body.classList.remove('drawer-open')

    // Re-collect slots
    this.collectSlots()

    // Update position based on current viewport
    this.updatePosition()

    console.log('[NavTeleport] Refreshed with slots:', Object.keys(this.slots))
  }

  destroy() {
    // Return to canonical location
    if (this.slots['aside'] && this.navContainer) {
      this.slots['aside'].element.appendChild(this.navContainer)
      this.slots['aside'].element.dataset.navTeleportActive = 'true'
    }
  }
}
